import { useState, useEffect } from "react";
import { notifications } from "@mantine/notifications";
import { AppRoutes } from "@/constants";
import { verifyEmail, resendVerificationCode } from "../api";

export type UseEmailVerificationOptions = {
  onSuccess?: () => void;
  redirectPath?: string;
  businessSlug?: string;
};

export const useEmailVerification = (options?: UseEmailVerificationOptions) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);

  // Decrement the resend timer every second
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Handle verification code submission
  const handleVerification = async (code: string, email: string) => {
    if (code.length !== 6) {
      notifications.show({
        title: "Invalid Code",
        message: "Please enter a valid 6-digit verification code",
        color: "red",
      });
      return false;
    }

    setIsSubmitting(true);
    try {
      const result = await verifyEmail(code, email);

      if (result.success) {
        if (result.requirePassword) {
          // Existing-account flow: redirect to login; do not set auth cookies
          notifications.show({
            title: "Email verified",
            message: "Please sign in with your password.",
            color: "green",
          });
          const loginPath = options?.businessSlug
            ? `/${options.businessSlug}${AppRoutes.businessLogin}`
            : AppRoutes.login;
          const params = new URLSearchParams({
            email,
            existingAccount: "1",
          });
          window.location.href = `${loginPath}?${params.toString()}`;
          return true;
        }

        notifications.show({
          title: "Success",
          message: result.message,
          color: "green",
        });

        if (options?.onSuccess) {
          options.onSuccess();
        }
        if (options?.redirectPath) {
          window.location.href = options.redirectPath;
        }

        return true;
      } else {
        notifications.show({
          title: "Verification Failed",
          message:
            result.message ||
            "The verification code is invalid or has expired. Please try again.",
          color: "red",
        });
        return false;
      }
    } catch (error) {
      console.error("Error verifying code:", error);
      notifications.show({
        title: "Verification Failed",
        message:
          "The verification code is invalid or has expired. Please try again.",
        color: "red",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle resend code
  const handleResendCode = async (email: string) => {
    try {
      const result = await resendVerificationCode(email, options?.businessSlug);

      if (result.success) {
        notifications.show({
          title: "Code Sent",
          message:
            result.message ||
            "A new verification code has been sent to your email",
          color: "blue",
        });
        setResendTimer(10);
        return true;
      } else {
        notifications.show({
          title: "Error",
          message:
            result.message ||
            "Failed to resend verification code. Please try again later.",
          color: "red",
        });
        return false;
      }
    } catch (error) {
      console.error("Error resending code:", error);
      notifications.show({
        title: "Error",
        message: "Failed to resend verification code. Please try again later.",
        color: "red",
      });
      return false;
    }
  };

  // Start the resend timer
  const startResendTimer = () => {
    setResendTimer(10);
  };

  return {
    isSubmitting,
    resendTimer,
    handleVerification,
    handleResendCode,
    startResendTimer,
  };
};
